import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getDefaultResolvedFeatureFlag, resolveFeatureFlagMap } from "@/lib/feature-flag-catalog";

type FeatureFlagRow = {
  id: string;
  label: string;
  description: string | null;
  is_global: boolean;
  created_at: string;
  updated_at?: string;
};

type FeatureFlagAssignmentRow = {
  flag_id: string;
  user_id: string;
  enabled: boolean;
  assigned_at: string;
};

export function useFeatureFlags() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["feature-flags", user?.id ?? "anon"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [{ data: flags, error: flagsError }, { data: assignments, error: assignmentsError }] = await Promise.all([
        supabase.from("feature_flags" as never).select("*").order("label", { ascending: true }),
        user
          ? supabase
              .from("feature_flag_assignments" as never)
              .select("*")
              .eq("user_id", user.id)
          : Promise.resolve({ data: [] as FeatureFlagAssignmentRow[], error: null }),
      ]);

      if (flagsError) throw flagsError;
      if (assignmentsError) throw assignmentsError;

      const assignmentMap = new Map(
        ((assignments as FeatureFlagAssignmentRow[] | null) || []).map((assignment) => [assignment.flag_id, assignment.enabled]),
      );

      const rawFlagMap = ((flags as FeatureFlagRow[] | null) || []).reduce<Record<string, boolean>>((acc, flag) => {
        acc[flag.id] = Boolean(flag.is_global || assignmentMap.get(flag.id));
        return acc;
      }, {});

      return resolveFeatureFlagMap(rawFlagMap);
    },
  });
}

export function useFeatureFlag(flagId: string): boolean {
  const { data } = useFeatureFlags();
  return Boolean(data?.[flagId] ?? getDefaultResolvedFeatureFlag(flagId));
}
